import {reaction, runInAction} from 'mobx';
import BasketStore from './BasketStore';
import DeliveryStore from './DeliveryStore';
import SettingsStore from './SettingsStore';
import UserStore from './UserStore';
import LastviewStore from './LastviewStore';
import {calculateBasket, yandexGetCostToOffice} from '../http/basketApi';
import {quoteOrdinaryOrder} from '../http/orderApi';
import {createBasketSessionCoordinator} from './siteBasketSession';
import {BASKET_SNAPSHOT_ERROR} from '../utils/siteBasketSnapshot';
import {createCombinedMindboxCartSync} from './combinedMindboxCart';

// Each instance owns its observers. Construction never resolves auth or sends HTTP.
export const createMainStore = () => {
  const main = {delivery: new DeliveryStore(), user: new UserStore(), settings: new SettingsStore(), lastviewed: new LastviewStore()};
  main.basket = new BasketStore(main.delivery, main.user);
  main.delivery.setBasketStore(main.basket);
  main.basketSession = createBasketSessionCoordinator(main);
  let disposed = false, calculationSeq = 0, ownedCalculationSeq = 0, deliverySeq = 0;
  const ready = () => !disposed && !main.basket.snapshotLocked && main.basket.snapshotMode === 'legacy';
  const current = context => ready() && main.basket._legacyContinuationCurrent(context);
  const calculationParams = () => JSON.stringify([
    main.basket.selectedItems, main.delivery.active, main.delivery.price, main.basket.promocode,
    main.basket.selectPaymentType, main.basket.bonusesSpend, main.user.user,
  ]);
  const deliveryParams = () => JSON.stringify([main.delivery.officePvz, main.delivery.type, main.basket.priceNoSell]);
  const failed = () => runInAction(() => {main.basket.snapshotError = BASKET_SNAPSHOT_ERROR; main.basket.calculatedItems = [];});
  const ownedPricingReady = () => !disposed && !main.basket.snapshotLocked && main.basket.snapshotMode === 'enforced' && main.basket.ordinaryOwnerReady;
  const disposers = [reaction(() => main.basket.bonusesOrderAvail, () => {
    if (!ready()) return;
    runInAction(() => {main.basket.setBonusesSpend(0); main.basket.setBonusesValue(''); main.basket.bonusesSpendAll = false;});
  }), reaction(() => [main.delivery.officePvz, main.delivery.type, main.basket.priceNoSell,
    main.basket.sessionEpoch, main.basket.snapshotMode, main.basket.snapshotPending, main.basket.snapshotError], async ([officePvz]) => {
    const seq = ++deliverySeq;
    if (!ready() || !officePvz) return;
    const context = main.basket._sessionOperationContext(), params = deliveryParams();
    if (main.delivery.type === 'show_room') {main.delivery.setPrice(0); return;}
    if (main.delivery.type !== 'yandex_pvz') return;
    try {
      const response = await yandexGetCostToOffice(officePvz.code, main.basket.priceNoSell);
      if (seq !== deliverySeq || !current(context) || params !== deliveryParams()) return;
      const pricing = response?.pricing_total;
      if (pricing) {
        const price = Math.round(+pricing.replace(' RUB', ''));
        if (!Number.isFinite(price)) {failed(); return;}
        runInAction(() => main.delivery.setPrice(parseInt(price)));
      }
    } catch {if (seq === deliverySeq && current(context) && params === deliveryParams()) failed();}
  }), reaction(() => [main.basket.selectedItems, main.delivery.active, main.delivery.price, main.basket.promocode,
    main.basket.selectPaymentType, main.basket.bonusesSpend, main.user.user, main.basket.totalCountBasketItems,
    main.basket.sessionEpoch, main.basket.localSnapshotVersion, main.basket.snapshotMode, main.basket.snapshotPending, main.basket.snapshotError], async () => {
    const seq = ++calculationSeq;
    if (!ready() || !main.basket.update) return;
    const context = main.basket._sessionOperationContext(), params = calculationParams();
    try {
      const response = await calculateBasket(main.basket.selectedItems, main.delivery.active, main.delivery.price,
        main.basket.promocode, main.basket.selectPaymentType?.id || null, main.user.user, main.basket.bonusesSpend ?? 0);
      if (seq !== calculationSeq || !current(context) || params !== calculationParams()) return;
      if (!response || typeof response !== 'object') {failed(); return;}
      const {totalPrice, priceNoSell, deliveryPrice, promocode: promo, loyalty, promotions, promoDiscountSumm, newItems, favoriteBasketProduct} = response;
      runInAction(() => {
        main.basket.totalPrice = totalPrice; main.basket.priceNoSell = priceNoSell; main.basket.delPrice = deliveryPrice;
        main.basket.setSavoriteItems(favoriteBasketProduct); main.basket.promoDiscountSumm = promoDiscountSumm;
        main.basket.setCalculatedItems(newItems);
        // Pricing selected lines must not remove currently unselected display rows.
        const priced = Array.isArray(newItems) ? main.basket.basketItems.map(line => {
          const matches = newItems.filter(item => main.basket._lineSelectionIdentity(item) === main.basket._lineSelectionIdentity(line));
          return matches.length === 1 ? {...matches[0], selected: line.selected} : line;
        }) : newItems;
        main.basket.setItemsPrices(priced);
        if (loyalty) {
          main.basket.bonusesOrderEarned = loyalty.bonusPointsChanges.earnedAmount;
          main.basket.bonusesOrderAvail = loyalty.totalBonusPointsInfo.availableAmountForCurrentOrder;
          main.basket.bonusesTotal = loyalty.totalBonusPointsInfo.balance.available;
          main.basket.bonusesConfirmed = loyalty.bonusPointsChanges.spentAmount;
          if (main.basket.bonusesConfirmed !== +main.basket.bonusesValue) main.basket.setBonusesValue(main.basket.bonusesConfirmed);
          main.basket.setNoProductsForBonuses(main.basket.bonusesTotal && !main.basket.bonusesOrderAvail);
          main.basket.isLoyaltyActive = true;
        } else main.basket.bonusesConfirmed = 0;
        main.basket.usedPromotions = promotions;
        if (!promo?.error && promo?.status) {
          main.basket.promocodeItem = promo.item;
          main.basket.promoNitificator = {color: 'green', message: 'Применен промокод ' + promo.item.promocode_value};
        }
        if (!promo?.item && main.basket.promocode?.length > 0) {
          main.basket.promocodeItem = null; main.basket._promocode = null;
          main.basket.promoNitificator = {color: 'red', message: promo?.error || 'Промокод не найден'};
        }
      });
    } catch {if (seq === calculationSeq && current(context) && params === calculationParams()) failed();}
  }), reaction(() => [main.basket.pricingParameters(), main.basket.sessionEpoch, main.basket.localSnapshotVersion,
    main.basket.snapshotMode, main.basket.snapshotPending, main.basket.snapshotError, main.basket.pricingRetryVersion,
    main.user.isAuth, main.user.user?.id], async () => {
    if (main.basket.snapshotMode !== 'enforced') return;
    const seq = ++ownedCalculationSeq;
    main.basket.clearPricing();
    if (!ownedPricingReady()) return;
    const context = Object.freeze({...main.basket._sessionOperationContext(), parameters: main.basket.pricingParameters(), deliveryId: main.delivery.active?.id ?? null});
    main.basket.startPricing(context);
    try {
      const input = main.basket.pricingInput();
      const complete = main.basket.ordinaryCalculationComplete(input), authScope = context.authScope;
      // Incomplete choices keep display-only calculation. Complete choices use
      // one atomic price/hash quote, never two competing calculators.
      const response = complete ? await quoteOrdinaryOrder(input, {authScope}) :
        await calculateBasket(input.items, input.delivery, null, input.promo, input.payment, null, input.bonusesAmount,
          {authScope, ...(input.shippingContext ? {shippingContext: input.shippingContext} : {})});
      if (seq !== ownedCalculationSeq || !ownedPricingReady() || !main.basket.pricingContextCurrent(context)) return;
      if (complete) main.basket.applyOrdinaryQuote(response, context); else main.basket.applyPricing(response, context);
    } catch {if (seq === ownedCalculationSeq && ownedPricingReady()) main.basket.failPricing(context);}
  }), reaction(() => [main.basket._ordinaryNeedsRecovery, main.basket.sessionEpoch, main.basket.snapshotMode,
    main.basket.snapshotPending, main.basket.snapshotError, main.user.isAuth, main.user.user?.id], () => {
    if (ownedPricingReady() && main.basket._ordinaryNeedsRecovery) main.basket.recoverOrdinaryCheckout();
  })];
  disposers.push(createCombinedMindboxCartSync(main));
  main.disposeEffects = () => {
    if (disposed) return;
    disposed = true; calculationSeq += 1; ownedCalculationSeq += 1; deliverySeq += 1;
    for (const dispose of disposers) dispose();
    main.basketSession.dispose();
  };
  return main;
};

const main = createMainStore();
export default main;
